import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import './pagination-box.scss';
import { range } from 'lodash-es';
import { track } from 'react-tracking';
import Arrow from './arrow-svg';

const PagesContainer = styled.ul`
  float: right;
  box-shadow: none;
  margin-top: 5px;
`;

const DOTS = '...';
const LEFT_ARROW = 'LEFT_ARROW';
const RIGHT_ARROW = 'RIGHT_ARROW';

/**
 * --- Pagination Box ---
 * Component should receive this props:
 * itemsPerPage - A number of items shown per page for calculating number of pages (optional/)
 * totalRecords - Total number of items to be paginated
 * offset - Current list's offset
 * onPageChanged(nextOffset) - a callback for changing a page
 */

class PaginationBox extends React.Component {
  static propTypes = {
    totalRecords: PropTypes.number.isRequired,
    offset: PropTypes.number.isRequired,
    onPageChanged: PropTypes.func.isRequired,
    itemsPerPage: PropTypes.number,
    tracking: PropTypes.object
  };

  static defaultProps = {
    totalRecords: 1,
    itemsPerPage: 20,
    offset: 0
  };

  constructor(props) {
    super(props);
    const { itemsPerPage, totalRecords } = props;
    this.state = {
      totalPages: Math.ceil(totalRecords / itemsPerPage),
      currentPage: 1,
      isLoading: false
    };
  }

  componentDidMount() {
    const { offset } = this.props;
    this.setCurrentPage(offset);
  }

  componentDidUpdate(prevProps) {
    const { itemsPerPage, totalRecords } = this.props;
    const { totalPages } = this.state;
    if (prevProps.totalRecords !== totalRecords || (totalPages === 0 && totalRecords > 0)) {
      this.setState({ totalPages: totalRecords / itemsPerPage });
    }
    if (prevProps.totalRecords === 0 && totalRecords > 0 && this.state.isLoading) {
      this.setState({ isLoading: false });
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setCurrentPage(nextProps.offset);
  }

  setCurrentPage = offset => {
    const { itemsPerPage } = this.props;
    const currentPage = offset / itemsPerPage + 1;
    this.setState({ currentPage });
  };

  goToPage = nextPage => {
    const { itemsPerPage, onPageChanged, tracking } = this.props;
    const { currentPage } = this.state;

    if (nextPage === currentPage) {
      return;
    }

    if (nextPage === currentPage + 1 || nextPage === currentPage - 1) {
      tracking.trackEvent({ eventName: 'Pagination button clicked', nextPage, currentPage });
    }

    const nextOffset = itemsPerPage * (nextPage - 1);
    onPageChanged(nextPage, nextOffset);
  };

  calculateNextPage = page => {
    const { currentPage } = this.state;
    switch (page) {
      case LEFT_ARROW:
        return currentPage - 1;
      case RIGHT_ARROW:
        return currentPage + 1;
      default:
        return page;
    }
  };

  generateNavItems = () => {
    const { currentPage, totalPages } = this.state;
    const isRightDotted = this.isRightDotsRequired(currentPage, totalPages);
    const isLeftDotted = currentPage > 4;
    const rangeFrom = this.rangeFrom(currentPage, totalPages);
    const rangeTo = currentPage > 4 && currentPage <= totalPages ? currentPage + 1 : 5;
    let items = range(rangeFrom, Math.min(rangeTo + 1, totalPages + 1));
    // case 5+
    if (isLeftDotted) {
      items = [1, DOTS, ...items];
    }
    // case 5+ and not the last page
    if (isRightDotted) {
      items = [...items, DOTS];
    }
    // not the last page
    if (currentPage < totalPages) {
      items = [...items, RIGHT_ARROW];
    }
    // case 2+
    if (currentPage > 1) {
      items = [LEFT_ARROW, ...items];
    }

    return items;
  };

  generateNumOrArrow = page => {
    if (typeof page === 'number') {
      return page;
    } else {
      return <Arrow isLeft={page === LEFT_ARROW} />;
    }
  };

  isRightDotsRequired = (currentPage, totalPages) =>
    (currentPage <= 5 && totalPages > 5) || (currentPage > 5 && totalPages - currentPage > 1)
      ? true
      : false;

  isCurrentPage = (index, currentPage) => (index === currentPage ? 'active' : '');

  isDots = page => (page === DOTS ? 'dots' : '');

  rangeFrom = (currentPage, lastPage) =>
    currentPage > 4 ? (currentPage === lastPage ? lastPage - 2 : currentPage - 1) : 1;

  dotsItem = i => (
    <li key={i} className="page-item dots">
      <a tabIndex={-1} href="#" className="page-link dots">
        ...
      </a>
    </li>
  );

  linkItem = (i, page, currentPage) => {
    const nextPage = this.calculateNextPage(page);
    return (
      <li key={i} className="page-item">
        <a
          onClick={() => this.goToPage(nextPage)}
          className={`page-link ${this.isCurrentPage(page, currentPage)}`}
        >
          {this.generateNumOrArrow(page)}
        </a>
      </li>
    );
  };

  render() {
    const { currentPage, isLoading } = this.state;
    const pages = this.generateNavItems();

    return (
      <PagesContainer className="pagination">
        {isLoading ? (
          <div className="spinner" style={{ marginTop: '10px' }} />
        ) : (
          pages.map((page, i) => {
            if (this.isDots(page)) {
              return this.dotsItem(i);
            } else {
              return this.linkItem(i, page, currentPage);
            }
          })
        )}
      </PagesContainer>
    );
  }
}

export default track()(PaginationBox);
